import { getApiClient } from '../lib/axios';
// Phase 25.5a — closes the long-standing Phase 17 carry-over: auth
// request/response shapes now flow through @commerce-os/shared-types
// instead of being redeclared here. RegisterRequest stays local
// because it isn't shared with another consumer yet (POS + management
// don't expose org-creation surfaces).
import type {
  LoginRequest,
  LoginResponse,
  MeResponse,
  PinVerifyRequest,
  PinVerifyResponse,
} from '@commerce-os/shared-types';

export type { LoginRequest, LoginResponse, MeResponse } from '@commerce-os/shared-types';

/**
 * `POST /auth/login` is a UNION. A TOTP-enrolled user gets an MFA challenge
 * instead of a token pair, and the tokens only arrive from
 * `POST /auth/mfa/validate { mfaToken, code }`, whose response is shaped
 * exactly like a normal login success.
 *
 * Two discriminators are accepted on purpose. The API signals the challenge
 * as `status: 'mfa_required'`; the RN clients
 * (`packages/rn-shared/src/auth/authActions.ts`) key on the older
 * `mfaRequired: true` flag, which `LoginResponse` in shared-types still
 * declares. Both shapes agree on `mfaToken`, and that is the field the
 * dashboard actually needs — so either signal routes to the code step, and
 * a signal WITHOUT a token is treated as a malformed response rather than a
 * password login (the old `data.mfaRequired && data.mfaToken` check fell
 * through to `setTokens(undefined, undefined)` for the new shape).
 */
export interface LoginMfaRequiredResponse {
  status: 'mfa_required';
  mfaToken: string;
  mfaRequired?: true;
}

export type LoginApiResponse = LoginResponse | LoginMfaRequiredResponse;

/** True when the server signalled an MFA challenge by either discriminator. */
export function isMfaChallengeSignalled(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const candidate = data as { status?: unknown; mfaRequired?: unknown };
  return candidate.status === 'mfa_required' || candidate.mfaRequired === true;
}

/** True for a well-formed MFA challenge: signalled AND carrying a usable token. */
export function isMfaRequiredResponse(data: unknown): data is LoginMfaRequiredResponse {
  if (!isMfaChallengeSignalled(data)) return false;
  const { mfaToken } = data as { mfaToken?: unknown };
  return typeof mfaToken === 'string' && mfaToken.length > 0;
}

export interface RegisterRequest {
  orgName: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

/** Dashboard-local response shape — MFA enrollment isn't surfaced on
 *  POS or management today, so the type stays here until a second
 *  consumer needs it. */
export interface MfaEnrollResponse {
  secret: string;
  qrCodeUrl: string;
}

export const authApi = {
  verifyPin: (data: PinVerifyRequest) =>
    getApiClient().post<PinVerifyResponse>('/auth/pin/verify', data).then((r) => r.data),
  login: (data: LoginRequest) =>
    getApiClient().post<LoginApiResponse>('/auth/login', data).then((r) => r.data),

  register: (data: RegisterRequest) =>
    getApiClient().post<LoginResponse>('/auth/register', data).then((r) => r.data),

  refresh: () =>
    getApiClient().post('/auth/refresh', {}).then((r) => r.data),

  logout: () =>
    getApiClient().post('/auth/logout'),

  getMe: () =>
    getApiClient().get<MeResponse>('/auth/me').then((r) => r.data),

  mfaEnroll: () =>
    getApiClient().post<MfaEnrollResponse>('/auth/mfa/enroll').then((r) => r.data),

  mfaVerify: (code: string) =>
    getApiClient().post('/auth/mfa/verify', { code }).then((r) => r.data),

  mfaValidate: (mfaToken: string, code: string) =>
    getApiClient()
      .post<LoginResponse>('/auth/mfa/validate', { mfaToken, code })
      .then((r) => r.data),

  // Phase 27a — public forgot/reset password flow.
  forgotPassword: (email: string) =>
    getApiClient()
      .post<{ ok: true }>('/auth/forgot-password', { email })
      .then((r) => r.data),

  resetPassword: (token: string, newPassword: string) =>
    getApiClient()
      .post<{ ok: true }>('/auth/reset-password', { token, newPassword })
      .then((r) => r.data),
};
